import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  EventChannel,
  IMomentDenormalized,
  isSubTaskBlock,
  SubTaskQuestionAnswer,
  SubTaskType
} from '@dadohr/shared/domainTypes/experience';
import ENV from '../../../env.json';
import { usePromise } from '../../../hooks/usePromise';
import {
  LoadingValue,
  refreshTimestamp,
  useMappedLoadingValue
} from '../../../services/firebase';

const refreshMomentDenormalizedTimestamps = (d: Doc<IMomentDenormalized>) => {
  d.data.sendAt = refreshTimestamp(d.data.sendAt);
  d.data.content.blocks.forEach((b) => {
    if (isSubTaskBlock(b)) {
      b.lastCompletedAt = refreshTimestamp(b.lastCompletedAt);
    }
  });
};

const isLocalhost = () => window.location.host.startsWith('localhost');
const getBaseUrl = () => {
  return new URL(
    isLocalhost() ? ENV.domains.cf.public : window.location.origin
  );
};

const getDenormalizedMoment = (accessToken: string) => {
  const url = getBaseUrl();
  url.pathname = isLocalhost() ? 'standaloneApp_getMoment' : '/api/getMoment';

  url.searchParams.set('accessToken', accessToken);
  url.searchParams.set('currentUrl', window.location.href);
  return fetch(url.toString(), {
    redirect: 'follow',
    headers: {
      Accept: 'application/json'
    }
  }).then(async (r) => {
    // SSO handling might go here later
    const d: { status: 'OK'; data: Doc<IMomentDenormalized> } = await r.json();
    if (d.status !== 'OK') {
      return Promise.reject();
    }
    const data = d.data;
    refreshMomentDenormalizedTimestamps(data);
    return data;
  });
};

const useDenormalizedMoment = (accessToken: string, v: number) => {
  return usePromise(() => getDenormalizedMoment(accessToken), [accessToken, v]);
};

export const useStandaloneMoment = (
  accessToken: string,
  v = 0
): LoadingValue<Doc<IMomentDenormalized>> => {
  // experience code does some funny updating of subtask statuses here
  // let's leave that for now
  return useMappedLoadingValue(useDenormalizedMoment(accessToken, v), (m) => {
    return m;
  });
};

export const completeMomentSubTask = async (
  instanceId: string,
  subTaskId: string
) => {
  const u = getBaseUrl();
  u.pathname = isLocalhost()
    ? 'moments_completeMomentSubTask'
    : '/api/completeMomentSubTask';
  const x = {
    m: instanceId,
    s: subTaskId,
    c: EventChannel.APP
  };

  const d = btoa(unescape(encodeURIComponent(JSON.stringify(x, null, 0))));
  u.searchParams.set('d', d);
  const url = u.toString();
  await fetch(url);
};

export const answerMomentSubTasks = async (
  instanceId: string,
  answers: { subTaskId: string; d: SubTaskQuestionAnswer['d'] }[]
) => {
  const u = getBaseUrl();
  u.pathname = isLocalhost()
    ? 'moments_answerMomentSubTasks'
    : '/api/answerMomentSubTasks';

  const x: {
    m: string;
    c: EventChannel;
    ds: { s: string; d: SubTaskQuestionAnswer['d'] }[];
  } = {
    m: instanceId,
    c: EventChannel.APP,
    ds: answers.map((a) => ({ s: a.subTaskId, d: a.d }))
  };
  const d = btoa(unescape(encodeURIComponent(JSON.stringify(x, null, 0))));
  u.searchParams.set('d', d);
  const url = u.toString();
  await fetch(url);
};

export const markMomentAsRead = async (
  momentInstanceAccessToken: string,
  instanceId: string,
  read: boolean
) => {
  const u = getBaseUrl();
  u.pathname = isLocalhost() ? 'moments_markAsRead' : '/api/markMomentAsRead';
  const x = {
    m: instanceId,
    t: momentInstanceAccessToken,
    v: read
  };
  const d = btoa(unescape(encodeURIComponent(JSON.stringify(x, null, 0))));
  u.searchParams.set('d', d);
  const url = u.toString();
  await fetch(url);
};

export const answerMomentSubTask = async (
  instanceId: string,
  answer: { subTaskId: string; d: SubTaskQuestionAnswer['d'] }
) => {
  return answerMomentSubTasks(instanceId, [answer]);
};

export const getMomentSubTaskVisitUrl = (
  url: string,
  instanceId: string,
  subTaskId: string
) => {
  const u = new URL(ENV.domains.cf.public);
  // Must match the deployed function + all server-side builders (hyphen). The
  // underscore name 404s: it sanitizes to the same Cloud Run service as
  // `redirect-redirect`, so the two can't coexist (see functions/redirect).
  u.pathname = '/redirect-redirect';
  const params = {
    t: 'moments-completeSubTask',
    p: {
      m: instanceId,
      s: subTaskId
    }
  };

  const d = btoa(JSON.stringify(params, null, 0));
  u.searchParams.set('url', encodeURIComponent(url));
  u.searchParams.set('d', d);
  return u.toString();
};

export const trackMomentSubTaskCompletion = (
  momentId: string,
  subTaskId: string,
  subTaskType: SubTaskType
) => {
  // TODO - not sure what we wanted to track here?
  // Certainly not firebase analytics
};
